import { CKEditor } from '@ckeditor/ckeditor5-react';
import { Typography } from '@mui/material';
import {
  AccessibilityHelp,
  Alignment,
  Autosave,
  BlockQuote,
  Bold,
  ClassicEditor,
  Editor,
  EditorConfig,
  Essentials,
  EventInfo,
  GeneralHtmlSupport,
  Heading,
  Highlight,
  Italic,
  Link,
  List,
  Paragraph,
  RemoveFormat,
  SelectAll,
  SpecialCharacters,
  Strikethrough,
  Underline,
  Undo,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import { FC } from 'react';

import { StyledEditorWrapper } from './styled';

type CKEditorProps = React.ComponentProps<typeof CKEditor>;

interface CustomCKEditorProps extends Omit<CKEditorProps, 'editor'> {
  value: string;
  onChangeCallback: (value: string) => void;
  config?: EditorConfig;
  isError?: boolean;
  errorMsg?: string;
}

const CustomEditor: FC<CustomCKEditorProps> = ({
  value,
  onChangeCallback,
  config = {},
  isError,
  errorMsg,
  ...rest
}) => {
  const editorConfig = {
    toolbar: {
      items: [
        'heading',
        '|',
        'bold',
        'italic',
        'underline',
        'strikethrough',
        'removeFormat',
        '|',
        'link',
        'highlight',
        'blockQuote',
        '|',
        'alignment',
        '|',
        'bulletedList',
        'numberedList',
      ],
      shouldNotGroupWhenFull: false,
    },
    plugins: [
      AccessibilityHelp,
      Alignment,
      Autosave,
      BlockQuote,
      Bold,
      Essentials,
      GeneralHtmlSupport,
      Heading,
      Highlight,
      Italic,
      Link,
      List,
      Paragraph,
      RemoveFormat,
      SelectAll,
      SpecialCharacters,
      Strikethrough,
      Underline,
      Undo,
    ],
    heading: {
      options: [
        {
          model: 'paragraph',
          title: 'Paragraph',
          class: 'ck-heading_paragraph',
        },
        {
          model: 'heading1',
          view: 'h1',
          title: 'Heading 1',
          class: 'ck-heading_heading1',
        },
        {
          model: 'heading2',
          view: 'h2',
          title: 'Heading 2',
          class: 'ck-heading_heading2',
        },
        {
          model: 'heading3',
          view: 'h3',
          title: 'Heading 3',
          class: 'ck-heading_heading3',
        },
        {
          model: 'heading4',
          view: 'h4',
          title: 'Heading 4',
          class: 'ck-heading_heading4',
        },
        {
          model: 'heading5',
          view: 'h5',
          title: 'Heading 5',
          class: 'ck-heading_heading5',
        },
        {
          model: 'heading6',
          view: 'h6',
          title: 'Heading 6',
          class: 'ck-heading_heading6',
        },
      ],
    },
    htmlSupport: {
      allow: [
        {
          name: /^.*$/,
          styles: true,
          attributes: true,
          classes: true,
        },
      ],
    },
    initialData: '',
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://',
      decorators: {
        toggleDownloadable: {
          mode: 'manual',
          label: 'Downloadable',
          attributes: {
            download: 'file',
          },
        },
      },
    },
    ...config,
  };

  return (
    <StyledEditorWrapper>
      <CKEditor
        editor={ClassicEditor}
        data={value}
        onChange={(_e: EventInfo, editor: Editor) => {
          const data = editor.getData();
          onChangeCallback(data);
        }}
        onError={(error: any) => {
          console.error({ ckeditorError: error });
        }}
        config={editorConfig as EditorConfig}
        {...rest}
      />
      {isError ? (
        <Typography paddingTop={1} color={'#D32F2F'} fontSize={'14px'}>
          {errorMsg}
        </Typography>
      ) : null}
    </StyledEditorWrapper>
  );
};

export { CustomEditor };
